import { useEffect, useRef, useState } from 'react';

interface AboutProps {
  scrollY: number;
}

export default function About({ scrollY }: AboutProps) {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const scrollToContactForm = () => {
    const contactForm = document.getElementById('contact-form');
    if (contactForm) {
      contactForm.scrollIntoView({ 
        behavior: 'smooth',
        block: 'center'
      });
    }
  };

  const features = [
    {
      icon: 'ri-shield-check-line',
      title: 'Certified Excellence',
      description: 'Fully licensed and certified construction company operating under Spanish regulations',
    },
    {
      icon: 'ri-team-line',
      title: 'Expert Team',
      description: 'Highly skilled professionals with decades of combined construction experience',
    },
    {
      icon: 'ri-time-line',
      title: 'On-Time Delivery',
      description: 'Committed to meeting deadlines without compromising on quality standards',
    },
    {
      icon: 'ri-customer-service-line',
      title: '24/7 Support',
      description: 'Round-the-clock customer service and technical assistance for all projects',
    },
  ];

  return (
    <section
      id="about"
      ref={sectionRef}
      className="relative py-32 bg-white overflow-hidden"
    >
      <div className="absolute inset-0">
        <div className="absolute top-1/4 right-0 w-96 h-96 bg-gradient-to-l from-blue-100 to-transparent rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 left-0 w-96 h-96 bg-gradient-to-r from-purple-100 to-transparent rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div
            className={`transform transition-all duration-1000 ${
              isVisible ? 'translate-x-0 opacity-100' : '-translate-x-10 opacity-0'
            }`}
          >
            <div className="inline-block mb-6 px-6 py-2 bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20 rounded-full">
              <span className="text-blue-600 font-semibold">About Conpro Evolucion</span>
            </div>

            <h2 className="text-5xl md:text-6xl font-bold mb-6">
              <span className="bg-gradient-to-r from-gray-900 via-blue-900 to-purple-900 bg-clip-text text-transparent">
                Building Dreams Since 2025
              </span>
            </h2>

            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Based in the heart of Marbella, Málaga, Conpro Evolucion SL is your trusted partner for specialized construction activities. We combine traditional craftsmanship with modern innovation to deliver exceptional results.
            </p>

            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
              Our comprehensive services span from specialized building construction to advanced equipment rental, ensuring every aspect of your project is handled with expertise and precision. We pride ourselves on transforming architectural visions into tangible realities.
            </p>

            <div className="space-y-4 mb-8">
              {[
                'Specialized construction activities in building construction',
                'Advanced machinery and equipment rental services',
                'Comprehensive project management and consulting',
                'Custom solutions for residential and commercial projects',
              ].map((item, index) => (
                <div
                  key={index}
                  className="flex items-start space-x-3 transform transition-all duration-500 hover:translate-x-2"
                  style={{ transitionDelay: `${index * 100}ms` }}
                >
                  <div className="w-6 h-6 mt-1 bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-line text-white text-sm"></i>
                  </div>
                  <span className="text-gray-700">{item}</span>
                </div>
              ))}
            </div>

            <button
              onClick={scrollToContactForm}
              className="group px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-full font-semibold text-lg hover:shadow-xl hover:shadow-blue-500/50 transform hover:scale-105 transition-all duration-300 flex items-center space-x-2 whitespace-nowrap cursor-pointer"
            >
              <span>Get in Touch</span>
              <i className="ri-arrow-right-line text-xl group-hover:translate-x-1 transition-transform duration-300"></i>
            </button>
          </div>

          <div
            className={`relative transform transition-all duration-1000 delay-300 ${
              isVisible ? 'translate-x-0 opacity-100' : 'translate-x-10 opacity-0'
            }`}
          >
            <div className="relative">
              <div className="absolute -top-6 -left-6 w-full h-full bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-3xl blur-xl"></div>
              
              <div className="relative bg-white rounded-3xl overflow-hidden shadow-2xl shadow-gray-300/50">
                <img
                  src="https://public.readdy.ai/ai/img_res/f4f4ccb5c1814a03771004ed1068f653.jpg"
                  alt="Conpro Evolucion Construction"
                  className="w-full h-auto object-cover object-top"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6 mt-12">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="bg-gradient-to-br from-white to-slate-50 rounded-2xl p-6 shadow-lg shadow-gray-200/50 hover:shadow-xl hover:shadow-gray-300/50 transform hover:-translate-y-1 transition-all duration-300"
                  style={{ transitionDelay: `${400 + index * 100}ms` }}
                >
                  <div className="w-12 h-12 mb-4 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl flex items-center justify-center">
                    <i className={`${feature.icon} text-2xl text-white`}></i>
                  </div>
                  <h4 className="font-bold text-gray-900 mb-2">{feature.title}</h4>
                  <p className="text-sm text-gray-600 leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
